import { MAX_SELECTION } from './tapToCommand';

/** The little the selection rules need to know about a unit. */
export interface Selectable {
  readonly id: number;
  readonly owner: number;
  readonly defId: string;
  /** World units. */
  readonly x: number;
  readonly y: number;
  /** Hidden (inside a mine) or dying units cannot be selected. */
  readonly selectable: boolean;
}

export interface WorldRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

function inside(u: Selectable, r: WorldRect): boolean {
  const x0 = Math.min(r.x0, r.x1);
  const x1 = Math.max(r.x0, r.x1);
  const y0 = Math.min(r.y0, r.y1);
  const y1 = Math.max(r.y0, r.y1);
  return u.x >= x0 && u.x <= x1 && u.y >= y0 && u.y <= y1;
}

function nearestFirst(units: Selectable[], cx: number, cy: number): Selectable[] {
  return units
    .map((u) => ({ u, d: (u.x - cx) * (u.x - cx) + (u.y - cy) * (u.y - cy) }))
    .sort((a, b) => a.d - b.d || a.u.id - b.u.id)
    .map((e) => e.u);
}

/** Own units inside the box, nearest the box's center first, capped. Deterministic. */
export function selectInBox(
  units: readonly Selectable[],
  box: WorldRect,
  owner: number,
  cap = MAX_SELECTION,
): number[] {
  const cx = (box.x0 + box.x1) / 2;
  const cy = (box.y0 + box.y1) / 2;
  const hits = units.filter((u) => u.owner === owner && u.selectable && inside(u, box));
  return nearestFirst(hits, cx, cy)
    .slice(0, cap)
    .map((u) => u.id);
}

/** Own units of the anchor's type within `view`, the anchor first then nearest to it, capped. */
export function selectSameType(
  units: readonly Selectable[],
  anchor: Selectable,
  view: WorldRect,
  cap = MAX_SELECTION,
): number[] {
  const hits = units.filter(
    (u) =>
      u.id !== anchor.id &&
      u.owner === anchor.owner &&
      u.defId === anchor.defId &&
      u.selectable &&
      inside(u, view),
  );
  return [anchor.id, ...nearestFirst(hits, anchor.x, anchor.y).map((u) => u.id)].slice(0, cap);
}
